<template>
  <div>
    <v-card-title>
      <h2>Forms</h2>
      <v-spacer />
      <v-btn
        v-if="checkPermission('userform', PERMISSIONS.CAN_BULK)"
        :to="{name: 'UserFormImport'}"
        round
        color="yellow"
      >
        <v-icon
          dark
          left
        >
          mdi-database-import
        </v-icon>
        Import
      </v-btn>
    </v-card-title>
    <br />
    <v-layout
      row wrap
    >
      <v-flex
        sm12
        md3
        style="padding:0 5px 0 5px;"
      >
        <v-card>
          <v-toolbar
            class="primary-color"
            dense
            flat
            dark
          >
            Search &amp; Filtering
          </v-toolbar>
          <div
            v-if="bulkop"
          >
            <v-alert
              :value="true"
              type="info"
              style="margin-top: 0px"
            >
              Filtering by Bulk Operation #{{bulkop}}
            </v-alert>
          </div>
          <div
            v-if="!filterClosedCases"
          >
            <v-alert
              :value="true"
              type="error"
              style="margin-top: 0px"
              outline
              dense
            >
              Filtering Closed Cases
            </v-alert>
          </div>
          <v-card-text>
            <v-text-field
              v-model="searchText"
              slot="activator"
              append-icon="search"
              label="Search Text"
              single-line
              v-on:keyup.enter="search"
            />
            <v-select
              v-model="searchStatus"
              :items="statusStrings"
              item-text="name"
              item-value="value"
              label="Search by Status"
            />
            <v-autocomplete
              v-model="filterFormTemplate"
              :loading="searching"
              :items="formTemplates"
              :search-input.sync="formSearch"
              item-value="name"
              item-text="name"
              auto-select-first
              label="Search by Template"
            >
              <template slot="no-data">
                <div
                  class="autocomplete-no-data"
                >
                  <div
                    v-if="formSearch"
                  >
                    <div
                      v-if="searching"
                    >
                      Searching for '{{ formSearch }}'...
                    </div>
                    <div
                      v-else
                    >
                      <i>No results for '{{ formSearch }}'</i>
                    </div>
                  </div>
                  <div
                    v-else
                  >
                    Start typing to search
                  </div>
                </div>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filterCaseType"
              :loading="searching"
              :items="caseTypes"
              :search-input.sync="caseTypeSearch"
              item-value="id"
              item-text="name"
              auto-select-first
              label="Search by Case Type"
            >
              <template slot="no-data">
                <div
                  class="autocomplete-no-data"
                >
                  <div
                    v-if="caseTypeSearch"
                  >
                    <div
                      v-if="searching"
                    >
                      Searching for '{{ caseTypeSearch }}'...
                    </div>
                    <div
                      v-else
                    >
                      <i>No results for '{{ caseTypeSearch }}'</i>
                    </div>
                  </div>
                  <div
                    v-else
                  >
                    Start typing to search
                  </div>
                </div>
              </template>
            </v-autocomplete>
            <v-combobox
              v-model.trim="filterTags"
              :items="tagResults"
              :search-input.sync="tagSearch"
              label="Search by Tag"
              item-text="tag"
              item-value="tag"
              :return-object="false"
              flat
              small-chips
              hide-selected
              auto-select-first
              @input="tagSearch = ''"
            >
              <template slot="no-data">
                <div
                  class="autocomplete-no-data"
                >
                  <div
                    v-if="tagSearch"
                  >
                    <i>No results for '{{ tagSearch }}'</i>
                  </div>
                  <div
                    v-else
                  >
                    Start typing to search
                  </div>
                </div>
              </template>
            </v-combobox>
            <v-select
              v-model="filterClosedCases"
              :items="[{'text': 'Open', 'value': true}, {'text': 'Closed', 'value': false}]"
              label="Case Status"
            />
            <v-radio-group
              style="margin:0;"
              v-model="filterSigned"
            >
              <template v-slot:label>
                <div>Filter Signed Documents</div>
              </template>
              <v-layout
                row wrap
              >
                <v-flex
                  sm6
                >
                  <v-radio
                    label="Signed"
                    value="true"
                  />
                </v-flex>
                <v-flex
                  sm6
                >
                  <v-radio
                    label="Unsigned"
                    value="false"
                  />
                </v-flex>
              </v-layout>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              @click="searchText='';searchStatus = null;filterCaseType = null;filterClosedCases = true;filterFormTemplate = '';tagSearch = '';filterTags=null;filterSigned=null;bulkop=null;search()"
            >
              Clear
            </v-btn>
            <v-btn
              color="primary"
              @click="search"
            >
              Search
            </v-btn>
            <v-spacer/>
          </v-card-actions>
        </v-card>
      </v-flex>
      <v-flex
        sm12
        md9
      >
        <v-card>
          <v-data-table
            :items="userforms"
            :headers="headers"
            :pagination.sync="pagination"
            :total-items="totalForms"
            :rows-per-page-items="paginationStrings"
            :loading="loading"
            disable-initial-sort>
            <template slot="actions-append">
              <v-tooltip left>
                <v-btn
                  round
                  flat
                  fab
                  small
                  @click="pdfDialog = true"
                  slot="activator"
                >
                  <v-icon
                    color="red"
                  >
                    mdi-file-pdf-box
                  </v-icon>
                </v-btn>
                <span>Download PDF Archive<br /><small>Of Current Search Terms</small></span>
              </v-tooltip>
              <v-tooltip left>
                <v-btn
                  round
                  flat
                  fab
                  small
                  @click="downloadCSV()"
                  slot="activator"
                >
                  <v-icon
                    color="green"
                  >
                    table_chart
                  </v-icon>
                </v-btn>
                <span>Download CSV<br /><small>Of Current Search Terms</small></span>
              </v-tooltip>
              <v-spacer />
            </template>
            <template slot="no-data">
              <tr>
                <td colspan="5">
                  <div style="text-align:center;padding:25px;">
                    <v-icon size="125px">find_in_page</v-icon>
                    <h3>No Results</h3>
                  </div>
                </td>
              </tr>
            </template>
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :style="(props.item.tags.length > 0) ? 'border-bottom:0;' : null"
              >
                <td>
                  <router-link :to="{ name: 'UserForm', params: { id: props.item.id } }">
                    <b>{{ props.item.name }}</b>
                  </router-link>
                  <span v-if="props.item.party_name || props.item.form_number"><br /><small>{{props.item.party_name}}&nbsp;<i>{{props.item.form_number}}</i></small></span>
                </td>
                <td style="text-transform: capitalize;">
                  {{ props.item.status }}
                </td>
                <td>
                  <CasePreview
                    :xcase="props.item.case"
                    :inset="true"
                    :key="props.item.case.id"
                  />
                </td>
                <td>
                  {{ convertToLocal(props.item.created_at) }} <br/>
                  <small style="color:black;">{{props.item.created_by_name}}</small>
                </td>
                <td>
                  {{ convertToLocal(props.item.updated_at) }} <br/>
                  <small style="color:black;">{{props.item.updated_by_name}}</small>
                </td>
              </tr>
              <tr
                v-if="props.item.tags.length > 0"
              >
                <td colspan="5">
                  <span
                    v-for="tag in resortTags(props.item.tags)"
                    :key="tag"
                  >
                    <v-chip
                      label
                      small
                      :color="(tag === filterTags) ? 'yellow' : null"
                      :outline="tag.includes(':')"
                    >
                      {{tag}}
                    </v-chip>
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
      v-model="pdfDialog"
      :persistent="pdfDownloading"
      width="600"
    >
      <v-card
        color="white"
      >
        <v-toolbar
          class="primary-color"
          dense
        >
          <span style="color:white;">Batch PDF Download</span>
        </v-toolbar>
        <v-card-text
          v-if="pdfDownloading"
        >
          <br />
          Requesting ZIP Archive...<br/>
          <small>Depending on your query, this may take some time. Please do not navigate away.</small>
          <v-progress-linear
            indeterminate
            color="blue"
            class="mb-0"
          />
          <br />
        </v-card-text>
        <v-card-text
          v-else
        >
          <v-alert
            :value="!isLimited"
            type="error"
          >
            <b>Please limit your search terms to start a batch PDF request.</b> <br />
            Click "cancel" to adjust your search terms.
          </v-alert>
          <v-alert
            :value="isLimited"
            type="warning"
          >
            <b>Large queries may take some time</b><br />
            Please do not navigate away once you've requested a download
          </v-alert>
          <br />
          <v-select
            v-model="pdfType"
            :items="['internal', 'submission', 'esign', 'audit']"
            :disabled="!isLimited"
            item-text="name"
            item-value="value"
            label="PDF Type"
          />
          <span>Placeholder Variables: </span>
          <v-chip
            v-for="key in pdfNameVars"
            :key="key"
            @click="pdfNaming += '{' + key + '}'"
          >
            {{key}}
          </v-chip>
          <v-text-field
            v-model="pdfNaming"
            :disabled="!isLimited"
            label="Naming Convention"
            single-line
            suffix=".pdf"
          />
          <small><b>Directory Creation:</b> {{ app_name }} supports placing files in a named directories when exporting. Place a backslash (/) to create directories.</small>
        </v-card-text>
        <v-card-actions
          v-if="!pdfDownloading"
        >
          <v-btn
            @click="pdfDialog = false"
            flat
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!isLimited || !allCurlyBracesAreClosed"
            dark
            @click="downloadPdfZip()"
          >
            Start Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="downloadingCSV"
      persistent
      width="300"
    >
      <v-card
        color="white"
      >
        <v-card-text>
          Generating CSV File...<br/>
          <small>Depending on your query, this may take some time.</small>
          <v-progress-linear
            indeterminate
            color="blue"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import GlobalConstants from '@/globals'
import pagination from '@/components/global/pagination'
import CasePreview from '@/components/admin/cases/CasePreview'
import ContactPreview from '@/components/admin/contacts/ContactPreview'

export default {
  name: 'UserFormList',
  mixins: [
    pagination
  ],
  components: {
    CasePreview, ContactPreview
  },
  data () {
    return {
      userforms: [],
      pagination: {},
      searchText: '',
      statusStrings: GlobalConstants.StatusStrings,
      loading: true,
      pdfDialog: false,
      pdfDownloading: false,
      pdfNaming: '',
      pdfType: 'internal',
      searchMenu: false,
      downloadingCSV: false,
      searchStatus: '',
      paginationStrings: [
        10, 25, 50
      ],
      headers: [
        { text: 'Form', value: 'template__name' },
        { text: 'Status', value: 'status' },
        { text: 'Case', value: 'case', sortable: false, width: 250 },
        { text: 'Issued', value: 'created_at' },
        { text: 'Last Update', value: 'updated_at' }
      ],
      pdfNameVars: [
        'form_number',
        'party_name',
        'first_name',
        'middle_name',
        'last_name',
        'template_name',
        'form_id'
      ],
      filter: null,
      formTemplates: [],
      caseTypes: [],
      caseTypeSearch: '',
      filterCaseType: null,
      formSearch: '',
      searching: false,
      filterFormTemplate: '',
      filterTags: null,
      filterSigned: null,
      filterClosedCases: true,
      tagSearch: null,
      tagResults: [],
      bulkop: null
    }
  },
  created () {
    this.documentTitle('User Forms')
  },
  computed: {
    totalForms () {
      return this.pagination.totalItems
    },
    isLimited () {
      return (this.filter !== {} && this.filter !== null) || this.searchText !== ''
    },
    allCurlyBracesAreClosed () {
      let lastBracket = ''
      let charsSinceBracket = 0
      for (const char of this.pdfNaming) {
        if(char == '{' || char == '}'){
          if(lastBracket == '{' && charsSinceBracket == 0){
            return false
          }
          if(char == lastBracket){
            return false
          }
          lastBracket = char
          charsSinceBracket = 0
        }
        else{
          charsSinceBracket++
        }
      }
      return this.pdfNaming.split('{').length == this.pdfNaming.split('}').length
    }
  },
  watch: {
    formSearch () {
      if (this.formSearch) {
        this.loadFormTemplates()
      }
    },
    caseTypeSearch () {
      if (this.caseTypeSearch) {
        this.loadCaseTypes()
      }
    },
    tagSearch () {
      if (this.tagSearch) {
        this.fetchTags()
      }
    }
  },
  methods: {
    fetch () {
      if (this.$route.query.bulkop) {
        this.bulkop = this.$route.query.count
      }
      this.searchMenu = false
      this.loading = true
      api.userform.userForm.list({
        search: this.searchText,
        ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy,
        pageNum: this.pagination.page,
        count: this.pagination.rowsPerPage,
        filter: this.filter || null,
        bulkop: this.bulkop || null
      })
        .then(data => {
          this.userforms = data.results
          this.pagination.page = data.page_number
          this.pagination.totalItems = data.count
          this.loading = false
          this.componentCreated = true
        })
    },
    fetchTags () {
      api.userform.userformTags.get(this.tagSearch)
        .then(tagResults => {
          this.tagResults = tagResults
        })
    },
    downloadPdfZip () {
      this.pdfDownloading = true
      api.userform.userForm.downloadPdfZip({ search: this.searchText, filter: this.filter || null, type: this.pdfType, name: this.pdfNaming })
      .then(response => {
        this.pdfDownloading = false
        this.pdfDialog = false
        let blob = new Blob([response.data])
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'Export_UserForms.zip'
        link.click()
      })
    },
    downloadCSV () {
      this.downloadingCSV = true
      api.userform.userForm.downloadCsv({search: this.searchText, ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, filter: this.filter || null})
        .then(response => {
          this.downloadingCSV = false
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Export_UserForms.csv'
          link.click()
        })
    },
    search () {
      this.pagination.page = 1
      var queryParam = {}, filterQueryParam = {}
      if (this.searchText) {queryParam['search'] = this.searchText}
      if (this.searchStatus) {filterQueryParam['status'] = this.searchStatus} else {delete filterQueryParam['status']}
      if (this.filterFormTemplate) {filterQueryParam['template__name'] = this.filterFormTemplate} else {delete filterQueryParam['template__name']}
      if (this.filterCaseType) {filterQueryParam['case__case_type__id'] = this.filterCaseType} else {delete filterQueryParam['case__case_type__id']}
      if (this.filterTags) {filterQueryParam['tags__name'] = this.filterTags} else {delete filterQueryParam['tags__name']}
      if (this.filterSigned) {filterQueryParam['valid_esign'] = this.filterSigned} else {delete filterQueryParam['valid_esign']}
      if (this.bulkop) {filterQueryParam['bulkop'] = this.bulkop} else {delete filterQueryParam['bulkop']}
      if (!this.filterClosedCases) {filterQueryParam['active'] = this.filterClosedCases} else {delete filterQueryParam['active']}
      if (Object.keys(filterQueryParam).length !== 0) {queryParam['filter'] = JSON.stringify(filterQueryParam)}
      this.$router.push({ query: queryParam })
    },
    loadFormTemplates () {
      this.searching = true
      api.formtemplate.formtemplate.list({ search: this.formSearch, count: 15 })
        .then(data => {
          this.searching = false
          this.formTemplates = data.results
        })
    },
    loadCaseTypes (id) {
      this.searching = true
      if (id) {
        api.cases.casetype.get(id)
          .then(data => {
            this.searching = false
            this.caseTypes = [data]
          })

      } else {
        api.cases.casetype.list({ search: this.caseTypeSearch, count: 15 })
          .then(data => {
            this.searching = false
            this.caseTypes = data.results
          })
      }
    }
  }
}
</script>

<style>

.search-bar {
  margin-top: 1em;
}

table {
  margin-top: 1em;
}

.v-radio label {
  text-transform: capitalize;
}

.tagOutline {
  border: 1px solid black;
}
</style>
